import React, { useEffect, useMemo } from 'react';
import { animate, motion, useMotionValue, useTransform } from 'motion/react';

import { computeFocusRect } from './focusRect';
import styles from './LineupCamOverlay.module.scss';

export type OverlayPhase = 'idle' | 'overlay' | 'expanding' | 'preview' | 'done';

interface LineupCamOverlayProps {
  backgroundStillUrl: string;
  focusPolygon: Array<{ x: number; y: number }>;
  overlayPhase: OverlayPhase;
  onExpandComplete: () => void;
  expandDurationMs: number;
}

const CORNER_SIZE = 16;

const LineupCamOverlay: React.FC<LineupCamOverlayProps> = ({
  backgroundStillUrl,
  focusPolygon,
  overlayPhase,
  onExpandComplete,
  expandDurationMs,
}) => {
  const rect = useMemo(() => computeFocusRect(focusPolygon), [focusPolygon]);

  const isExpanding = overlayPhase === 'expanding';
  const expandDuration = expandDurationMs / 1000;

  // Animate 0→1 during expand; drives clip-path and corner positions
  const progress = useMotionValue(0);

  // Interpolated rect values for corners
  const animLeft = useTransform(progress, (p: number) => `${rect.left * (1 - p) * 100}%`);
  const animTop = useTransform(progress, (p: number) => `${rect.top * (1 - p) * 100}%`);
  const animRight = useTransform(progress, (p: number) => `${(1 - rect.right) * (1 - p) * 100}%`);
  const animBottom = useTransform(progress, (p: number) => `${(1 - rect.bottom) * (1 - p) * 100}%`);
  const animPillLeft = useTransform(
    progress,
    (p: number) => `${((rect.left + rect.right) / 2) * (1 - p) * 100 + p * 50}%`,
  );
  const animPillTop = useTransform(progress, (p: number) => {
    const cutoutTop = rect.top * (1 - p) * 100;
    return `calc(${cutoutTop}% - 28px)`;
  });

  const clipPath = useTransform(progress, (p: number) => {
    const l = rect.left * (1 - p) * 100;
    const t = rect.top * (1 - p) * 100;
    const r = (rect.right + (1 - rect.right) * p) * 100;
    const b = (rect.bottom + (1 - rect.bottom) * p) * 100;
    return `polygon(evenodd, 0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, ${l}% ${t}%, ${l}% ${b}%, ${r}% ${b}%, ${r}% ${t}%, ${l}% ${t}%)`;
  });

  useEffect(() => {
    if (isExpanding) {
      progress.set(0);
      const controls = animate(progress, 1, {
        duration: expandDuration,
        ease: 'easeInOut',
        onComplete: onExpandComplete,
      });
      return () => controls.stop();
    }
    return undefined;
  }, [isExpanding, expandDuration, onExpandComplete, progress]);

  return (
    <div className={styles.overlay} data-testid="lineup-cam-overlay">
      {/* Primary cam still with a cutout hole — live video shows through the gap */}
      <motion.div
        className={styles.background}
        style={{ backgroundImage: `url(${backgroundStillUrl})`, clipPath }}
        data-testid="lineup-cam-overlay-background"
      />

      {/* White corner brackets */}
      {/* Top-left */}
      <motion.div
        className={styles.corner}
        style={{
          left: animLeft,
          top: animTop,
          borderTopWidth: 2,
          borderLeftWidth: 2,
          width: CORNER_SIZE,
          height: CORNER_SIZE,
        }}
      />
      {/* Top-right */}
      <motion.div
        className={styles.corner}
        style={{
          right: animRight,
          top: animTop,
          borderTopWidth: 2,
          borderRightWidth: 2,
          width: CORNER_SIZE,
          height: CORNER_SIZE,
        }}
      />
      {/* Bottom-left */}
      <motion.div
        className={styles.corner}
        style={{
          left: animLeft,
          bottom: animBottom,
          borderBottomWidth: 2,
          borderLeftWidth: 2,
          width: CORNER_SIZE,
          height: CORNER_SIZE,
        }}
      />
      {/* Bottom-right */}
      <motion.div
        className={styles.corner}
        style={{
          right: animRight,
          bottom: animBottom,
          borderBottomWidth: 2,
          borderRightWidth: 2,
          width: CORNER_SIZE,
          height: CORNER_SIZE,
        }}
      />

      {/* ACTION CAM pill — centered above the cutout */}
      <motion.div
        className={styles.pill}
        style={{
          left: animPillLeft,
          top: animPillTop,
        }}
        animate={{ opacity: isExpanding ? 0 : 1 }}
        transition={{ duration: 0.2 }}
      >
        ACTION CAM
      </motion.div>
    </div>
  );
};

export default LineupCamOverlay;
